import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, EmptyState, Label, ListBox } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { CategoryFilterOption } from '../productsMeta';
import { resolveFilterWords } from './filterLabels';
import type { FilterOptionsState } from './useFilterOptionsState';
import {
    categorySelectionLabel,
    type CategoryFilterValue,
} from './categoryPickerMeta';
import { CategoryBrowseOptions } from './CategoryBrowseOptions';
import { CategorySearchField } from './CategorySearchField';
import { CategorySearchOptions } from './CategorySearchOptions';
import { FilterOptionsStatus } from './FilterOptionsStatus';
import { FilterPickerFooter } from './FilterPickerFooter';
import { FilterPickerTags } from './FilterPickerTags';
import { useCategoryFilterPicker } from './useCategoryFilterPicker';

type CategoryFilterPickerProps = {
    options: FilterOptionsState<CategoryFilterOption>;
    value: CategoryFilterValue;
    onChange: (value: CategoryFilterValue) => void;
};

/**
 * The filters drawer's category field (BOS-202), on the brand picker's
 * pattern: searchable and multi-select, the picked values as removable tags.
 *
 * Picking a category means everything under it, as the server reads it
 * (bos-api BOS-173). While nothing is typed the list is sectioned by
 * top-level category, "Uncategorized" pinned above it, and each parent row
 * shows how much of its branch is picked; a search lists the matches grouped
 * under their path. A fully picked parent is one tag, "Electronics (all)".
 * `useCategoryFilterPicker` holds the state.
 */
export const CategoryFilterPicker: React.FC<CategoryFilterPickerProps> = ({
    options,
    value,
    onChange,
}) => {
    const { t } = useTranslation('pages');
    const picker = useCategoryFilterPicker(options.items, value, onChange);

    const tags = picker.selection.map(({ key, item }) => {
        const label = categorySelectionLabel(item);
        return {
            key,
            label: resolveFilterWords(label, (key, values) => t(key, values)),
        };
    });

    return (
        <Autocomplete
            className="w-full gap-2"
            onChange={picker.onSelectionChange}
            onOpenChange={picker.onOpenChange}
            placeholder={t(
                'products.components.productTable.filters.categoryPlaceholder',
            )}
            selectionMode="multiple"
            value={picker.pickedKeys}
            variant="secondary"
            {...getNodeTestIdProp('product-filters-category')}
        >
            <Label>
                {t('products.components.productTable.filters.categoryLabel')}
            </Label>
            <Autocomplete.Trigger>
                <Autocomplete.Value>
                    {({ defaultChildren }) =>
                        tags.length === 0 ? (
                            defaultChildren
                        ) : (
                            <FilterPickerTags
                                onRemove={picker.onRemove}
                                tags={tags}
                            />
                        )
                    }
                </Autocomplete.Value>
                <Autocomplete.ClearButton
                    aria-label={t(
                        'products.components.productTable.filters.categoryClear',
                    )}
                />
                <Autocomplete.Indicator />
            </Autocomplete.Trigger>
            <Autocomplete.Popover>
                <Autocomplete.Filter
                    inputValue={picker.search}
                    onInputChange={picker.onSearchChange}
                >
                    <CategorySearchField
                        matchCount={picker.results?.count ?? null}
                    />
                    <ListBox
                        aria-label={t(
                            'products.components.productTable.filters.categoryList',
                        )}
                        renderEmptyState={() =>
                            // Loading or failed says so below instead: an
                            // empty list there isn't "no match".
                            options.status === 'ready' ? (
                                <EmptyState>
                                    {t(
                                        'products.components.productTable.filters.categoryNoResults',
                                    )}
                                </EmptyState>
                            ) : null
                        }
                    >
                        {picker.results ? (
                            <CategorySearchOptions
                                groups={picker.results.groups}
                                tickStates={picker.tickStates}
                            />
                        ) : (
                            <CategoryBrowseOptions
                                noCategory={value.noCategory}
                                tickStates={picker.tickStates}
                                tree={picker.tree}
                            />
                        )}
                    </ListBox>
                </Autocomplete.Filter>
                {options.status === 'ready' ? null : (
                    <FilterOptionsStatus
                        onRetry={options.onRetry}
                        status={options.status}
                        subject="category"
                    />
                )}
                <FilterPickerFooter
                    count={tags.length}
                    onClear={picker.onClear}
                    subject="category"
                />
            </Autocomplete.Popover>
        </Autocomplete>
    );
};
